import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { CircularProgress, Box, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { AgGridUtils } from '@cosmotech/core';
import { useDataStoreFileDownload } from '../../../../../../DataStoreHooks';

export const DataGridTable = ({ storagePath, tableValidation }) => {
  const { t } = useTranslation();
  const downloadFile = useDataStoreFileDownload();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tableData, setTableData] = useState({ rows: [], cols: [] });
  const [isTruncated, setIsTruncated] = useState(false);
  const [originalTableLength, setOriginalTableLength] = useState(null);
  useEffect(() => {
    setLoading(true);
    downloadFile(storagePath, { params: { t: Date.now() }, headers: { 'Cache-Control': 'no-cache' } })
      .then(({ data: fileContent }) => {
        // Check if file content has more than 10k lines and truncate if necessary
        let truncated = false;
        // Length check to avoid splitting small files
        // 70k is an arbitrary number to avoid splitting small files
        if (fileContent.length > 70000) {
          const lines = fileContent.split('\n');
          if (lines.length > 10001) {
            // Keep header + 10000 lines of data
            setOriginalTableLength(lines.length - 1); // -1 to remove header line
            fileContent = lines.slice(0, 10001).join('\n');
            truncated = true;
          }
        }
        setIsTruncated(truncated);

        const endHeaderIndex = fileContent.indexOf('\n') !== -1 ? fileContent.indexOf('\n') : fileContent.length;
        const header = fileContent.substring(0, endHeaderIndex).split(',');
        const nestedCols = header.map((colName) => {
          return { field: colName, type: 'string', acceptsEmptyFields: true, flex: 1, minWidth: 150 };
        });
        // remove empty lines at the end of fileContent
        fileContent = fileContent.replace(/(\r?\n)+$/, '');
        const { rows, cols } = AgGridUtils.fromCSV(fileContent, true, nestedCols);
        if (rows === undefined || cols === undefined) {
          setError(true);
        } else {
          setError(false);
          rows.forEach((row, index) => {
            if (row.id === undefined) row.id = index;
          });
          setTableData({ rows, cols });
        }
        setLoading(false);
      })
      .catch((error) => {
        setError(true);
        console.error(error);
        setLoading(false);
      });
  }, [storagePath, tableValidation, downloadFile, error]);

  return (
    <>
      {loading ? (
        <CircularProgress sx={{ mx: 'auto', mt: 2, display: 'block' }} />
      ) : error ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {t('commoncomponents.datastore.details.tableDisplay.loadError', 'Error loading table')}
          </Typography>
          <Typography color="error">
            {t(
              'commoncomponents.datastore.details.tableDisplay.formatError',
              'Please verify that the table format is correct and try again.'
            )}
          </Typography>
        </Box>
      ) : (
        <>
          {isTruncated && (
            <Box sx={{ mb: 2, p: 2 }}>
              <Typography variant="body2">
                ⚠️{' '}
                {t(
                  'commoncomponents.datastore.details.tableDisplay.truncated',
                  `The file is too large and has been truncated to the first 10,000 lines for performance reasons. ` +
                    `The complete file contains {{count}} lines.`,
                  { count: originalTableLength }
                )}
              </Typography>
            </Box>
          )}
          <DataGrid
            columns={tableData.cols}
            rows={tableData.rows}
            sx={{ backgroundColor: (theme) => theme.palette.microsoft.main }}
          />
        </>
      )}
    </>
  );
};

DataGridTable.propTypes = {
  storagePath: PropTypes.string.isRequired,
  tableValidation: PropTypes.object,
};
